import CheckBox from '@app/components/CheckBox'
import SearchBar from '@app/components/SearchBar'
import { IAssignment } from '@app/models/Assignment'
import { AssignmentPriority } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import { formatDate } from '@app/utils/formatting'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link } from 'react-router-dom'
import { PropertyPageProps } from './PropertyPage'

interface IPropertyAssignmentsState {
  filterText: string
}

export default class PropertyAssignments extends React.PureComponent<
  PropertyPageProps,
  IPropertyAssignmentsState
> {
  constructor(props: PropertyPageProps) {
    super(props)

    this.state = {
      filterText: '',
    }

    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.setIncludeCompletedAssignments(false)
    this.props.loadAssignments(this.props.match.params.id, false)
  }

  public componentDidUpdate(prevProps: PropertyPageProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.props.setIncludeCompletedAssignments(false)
      this.props.loadAssignments(this.props.match.params.id, false)
    }
  }

  public render() {
    return (
      <DocumentTitle title={`${this.props.property.name} :: Assignments`}>
        <div>
          <SearchBar
            className='mt-3'
            onChange={this._setFilterText}
            placeholder={'Filter assignments...'}
            size='sm'
            value={this.state.filterText}
          />
          <div className='mt-1'>
            <CheckBox
              onChange={this._setIncludeCompleted}
              value={this.props.includeCompletedAssignments}
            >
              Include completed
            </CheckBox>
          </div>
          <div className='mt-3'>{this._renderAssignments()}</div>
        </div>
      </DocumentTitle>
    )
  }

  private _renderAssignments() {
    const filteredAssignments = !this.state.filterText
      ? this.props.assignments
      : this.props.assignments
          .filter(
            (a) =>
              a !== undefined &&
              a.title
                .toLowerCase()
                .indexOf(this.state.filterText.toLowerCase()) > -1,
          )
          .toList()

    if (filteredAssignments.size === 0) {
      return <div>No assignments found</div>
    }

    return filteredAssignments.sort(this._sortAssignments).map((a, i) => {
      if (a === undefined || i === undefined) {
        return null
      }

      const assignmentClasses = classNames('d-flex p-2', {
        'striped-row': i % 2 === 1,
      })

      let priorityIconName: IconName
      let iconClasses: string | undefined

      switch (a.priority) {
        case AssignmentPriority.High:
          iconClasses = 'text-danger'
          priorityIconName = 'exclamation-circle'
          break

        case AssignmentPriority.Low:
          iconClasses = 'text-info'
          priorityIconName = 'arrow-alt-circle-down'
          break

        default:
          priorityIconName = 'minus-circle'
          break
      }

      return (
        <div className={assignmentClasses} key={a.id}>
          <div
            className={classNames('mr-2', iconClasses)}
            title={`${a.priority} Priority`}
          >
            <FontAwesomeIcon icon={['far', priorityIconName]} fixedWidth />
          </div>
          <div className='flex-grow-1'>
            <Link
              className='text-dark font-weight-bold'
              to={`/assignment/${a.id}`}
            >
              {a.title}
            </Link>
          </div>
          <div>{a.dueDate ? formatDate(a.dueDate, 'MM/DD/YYYY') : ''}</div>
        </div>
      )
    })
  }

  private _setIncludeCompleted(newIncludeCompleted: boolean) {
    this.props.setIncludeCompletedAssignments(newIncludeCompleted)
    this.props.loadAssignments(this.props.match.params.id, newIncludeCompleted)
  }

  private _setFilterText(newFilterText: string) {
    this.setState({ filterText: newFilterText })
  }

  private _sortAssignments(a: IAssignment, b: IAssignment): number {
    // Assignments are sorted by:
    // 1. isComplete (incomplete then complete)
    // 2. dueDate (earliest to latest undefined last)
    // 3. title (a-z)

    if (a.isComplete === b.isComplete) {
      // Complete is the same so check due date
      if (a.dueDate === b.dueDate) {
        // Due date is the same so check title
        if (a.title < b.title) {
          return -1
        } else if (a.title > b.title) {
          return 1
        } else {
          return 0
        }
      } else if (a.dueDate === undefined) {
        return 1 // undefined due date last
      } else if (b.dueDate === undefined) {
        return -1 // undefined due date last
      } else if (a.dueDate < b.dueDate) {
        return -1
      } else {
        return 1
      }
    } else if (a.isComplete < b.isComplete) {
      return -1 // incomplete before complete
    } else {
      return 1 // incomplete before complete
    }
  }
}
